<script lang="ts">
  import { servers } from '$lib/store'
  import { Skeleton } from '$lib/components/ui/skeleton/index.js'
  import { Progress as Progression } from '$lib/components/ui/progress/index.js'
  import { route } from '@mateothegreat/svelte5-router'
  import { mode } from 'mode-watcher'
  import PLACEHOLDER_MACCHIATO from '$lib/assets/icons/book/volchap_placeholder.svg'
  import PLACEHOLDER_LATTE from '$lib/assets/icons/book/volchap_placeholder_latte.svg'
  import { getPageCountByBook } from '$lib/apis/book/api'
  import * as DropdownMenu from '$lib/components/ui/dropdown-menu/index.js'
  import Layers from '@lucide/svelte/icons/layers'
  import Check from '@lucide/svelte/icons/check'
  import {
    bookVariantLabel,
    bookVariantPrefKey,
    cn,
  } from '$lib/utils'

  let {
    book,
    currentPage,
    isCompleted,
    width,
    seriesid,
    serverid,
    libraryid,
    selected = false,
    selectionActive = false,
    onToggleSelect = (_bookId: string, _opts?: { shiftKey?: boolean }) => {},
  }: {
    book: any
    currentPage?: number
    isCompleted?: boolean
    width: number
    seriesid: string
    serverid: string
    libraryid: string
    selected?: boolean
    selectionActive?: boolean
    onToggleSelect?: (bookId: string, opts?: { shiftKey?: boolean }) => void
  } = $props()

  let loaded = $state(false)
  let failed = $state(false)
  let loading = $state(false)
  let showDetails = $state(false)
  let menuOpen = $state(false)
  let pageCount = $state(0)
  let selectedId = $state('')
  let longPressTimer: ReturnType<typeof setTimeout> | null = null
  let suppressClick = $state(false)

  const LONG_PRESS_MS = 450
  const MOVE_CANCEL_PX = 10

  const variants = $derived(
    Array.isArray(book?.variants) && book.variants.length > 0
      ? book.variants
      : [book]
  )
  const hasVariants = $derived(variants.length > 1)

  const type = $derived(book.type)
  const num = $derived(type == 'volume' ? book.volume : book.chapter)
  const subNum = $derived(
    type == 'volume' ? book.subVolume : (book.subChapter ?? '0')
  )

  const prefKey = $derived(
    bookVariantPrefKey(seriesid, type, num, subNum ?? 0)
  )

  $effect(() => {
    const ids = variants.map((v: any) => v.id)
    let pref = ''
    try {
      pref = localStorage.getItem(prefKey) ?? ''
    } catch {
      /* ignore */
    }
    selectedId =
      pref && ids.includes(pref) ? pref : (book?.id ?? variants[0]?.id ?? '')
  })

  const active = $derived(
    variants.find((v: any) => v.id === selectedId) ?? book
  )
  const activePage = $derived(active?.progression ?? currentPage ?? 0)
  const activeCompleted = $derived(active?.isCompleted ?? isCompleted)

  const href = $derived(
    `/media/${serverid}/library/${libraryid}/series/${seriesid}/${type}/${num}${
      subNum ? '.' + subNum : ''
    }`
  )

  $effect(() => {
    const server = $servers[serverid as string]
    const id = active?.id
    if (!server || !id) return
    getPageCountByBook(server, id).then(r => (pageCount = r?.pageCount ?? 0))
  })

  let progress = $derived(Math.min(100, 100 * (activePage / pageCount)) || -1)

  const PLACEHOLDER = $derived(
    mode.current === 'dark' ? PLACEHOLDER_MACCHIATO : PLACEHOLDER_LATTE
  )

  let coverSrc = $derived.by(() => {
    const base = $servers[serverid]?.urls?.[0]
    if (!base || !active?.id) return PLACEHOLDER
    let src = `${base}/api/v1/read/${active.id}/cover`
    if (base.indexOf('http:') != -1) {
      src = `https://proxy.nuread.direct.habysmol.com/api/v1/read/${active.id}/cover?upstream_addr=${base.replace('http://', '')}`
    }
    return src
  })

  const displaySrc = $derived(failed ? PLACEHOLDER : coverSrc)

  // Grid cell width stays in layout px; cover height from aspect so it tracks with width.
  const slotVars = $derived(`--slot-w:${width}px`)
  const coverBoxStyle = $derived(
    `${slotVars};width:var(--slot-w);height:calc(var(--slot-w) * 1.446)`
  )

  const showSelectBtn = $derived(showDetails || selected || selectionActive)

  $effect(() => {
    const src = coverSrc
    if (!src || src === PLACEHOLDER_MACCHIATO || src === PLACEHOLDER_LATTE) {
      loading = false
      loaded = true
      failed = true
      return
    }
    loading = true
    loaded = false
    failed = false
    const img = new Image()
    img.src = src
    img.onload = () => {
      loading = false
      loaded = true
    }
    img.onerror = () => {
      loading = false
      failed = true
      loaded = true
    }
  })

  function selectVariant(variant: any) {
    selectedId = variant.id
    try {
      localStorage.setItem(prefKey, variant.id)
    } catch {
      /* ignore */
    }
  }

  function clearLongPress() {
    if (longPressTimer != null) {
      clearTimeout(longPressTimer)
      longPressTimer = null
    }
  }

  function toggleSelect(opts?: { shiftKey?: boolean }) {
    const id = active?.id
    if (!id) return
    onToggleSelect(id, opts)
  }

  function onCoverClick(e: MouseEvent) {
    if (suppressClick) {
      e.preventDefault()
      e.stopPropagation()
      suppressClick = false
      return
    }
    if (selectionActive) {
      e.preventDefault()
      e.stopPropagation()
      toggleSelect({ shiftKey: e.shiftKey })
    }
  }

  function onTouchStart(e: TouchEvent) {
    if (e.touches.length !== 1) return
    const touch = e.touches[0]
    const startX = touch.clientX
    const startY = touch.clientY
    clearLongPress()
    longPressTimer = setTimeout(() => {
      longPressTimer = null
      suppressClick = true
      toggleSelect()
    }, LONG_PRESS_MS)

    const onMove = (ev: TouchEvent) => {
      const t = ev.touches[0]
      if (!t) return
      if (
        Math.abs(t.clientX - startX) > MOVE_CANCEL_PX ||
        Math.abs(t.clientY - startY) > MOVE_CANCEL_PX
      ) {
        clearLongPress()
        cleanup()
      }
    }
    const onEnd = () => {
      clearLongPress()
      cleanup()
    }
    const cleanup = () => {
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', onEnd)
      window.removeEventListener('touchcancel', onEnd)
    }
    window.addEventListener('touchmove', onMove, { passive: true })
    window.addEventListener('touchend', onEnd)
    window.addEventListener('touchcancel', onEnd)
  }

  function onContextMenu(e: MouseEvent) {
    // Avoid native menu after long-press on touch.
    if (selectionActive || suppressClick) {
      e.preventDefault()
    }
  }
</script>

<div
  role="gridcell"
  tabindex="-1"
  style="width: var(--slot-w); {slotVars}"
  class={cn(
    'relative transition-transform hover:scale-105',
    activeCompleted && 'opacity-60'
  )}
  onmouseenter={() => (showDetails = true)}
  onmouseleave={() => {
    if (!menuOpen) showDetails = false
  }}
  ontouchstart={onTouchStart}
  oncontextmenu={onContextMenu}
>
  <div
    class={cn(
      'relative overflow-hidden rounded-t shadow-2xl',
      selected &&
        'ring-2 ring-latte-mauve ring-offset-2 ring-offset-latte-crust dark:ring-macchiato-mauve dark:ring-offset-macchiato-crust'
    )}
    style={coverBoxStyle}
  >
    <a
      tabindex="0"
      class="absolute inset-0 block"
      href={href}
      use:route
      aria-label="{type} {num}{subNum ? '.' + subNum : ''}"
      onclick={onCoverClick}
    >
      {#if loading}
        <Skeleton class="size-full rounded-t" />
      {:else if loaded}
        <img
          class="size-full bg-latte-crust dark:bg-macchiato-base
          {failed ? 'object-contain' : 'object-cover'}
          {showDetails && !failed ? 'brightness-[0.33]' : ''}"
          src={displaySrc}
          alt=""
        />
      {/if}
    </a>

    <!-- Desktop hover select control (hidden on coarse pointers / touch). -->
    <button
      type="button"
      class={cn(
        'absolute top-1.5 left-1.5 z-20 hidden size-7 items-center justify-center rounded-full border shadow-sm transition-opacity [@media(hover:hover)_and_(pointer:fine)]:flex',
        selected
          ? 'border-latte-mauve bg-latte-mauve text-white dark:border-macchiato-mauve dark:bg-macchiato-mauve dark:text-macchiato-base'
          : 'border-white/50 bg-black/45 text-white hover:bg-black/60',
        showSelectBtn ? 'opacity-100' : 'opacity-0 pointer-events-none'
      )}
      aria-label={selected ? 'Deselect' : 'Select'}
      aria-pressed={selected}
      onclick={(e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        toggleSelect({ shiftKey: e.shiftKey })
      }}
    >
      {#if selected}
        <Check class="size-4" />
      {:else}
        <span
          class="size-3.5 rounded-full border-2 border-current opacity-80"
          aria-hidden="true"
        ></span>
      {/if}
    </button>

    <Progression
      value={progress}
      max={100}
      class="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-1 w-full rounded-none bg-macchiato-crust"
    />

    {#if hasVariants}
      <div class="absolute bottom-2 left-1.5 z-10">
        <DropdownMenu.Root bind:open={menuOpen}>
          <DropdownMenu.Trigger
            class="inline-flex h-7 items-center gap-1 rounded-md border border-latte-mauve/40 bg-latte-mauve/80 px-1.5 text-xs font-medium text-white shadow-sm hover:bg-latte-mauve/90 dark:border-macchiato-mauve/40 dark:bg-macchiato-mauve/80 dark:text-macchiato-base dark:hover:bg-macchiato-mauve/90"
            onclick={(e: MouseEvent) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            aria-label="Choose file variant"
          >
            <Layers class="size-3.5 shrink-0" />
            <span class="tabular-nums">{variants.length}</span>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content class="min-w-48 max-w-72 opacity-90" align="start">
            <DropdownMenu.Group>
              {#each variants as variant (variant.id)}
                <DropdownMenu.Item
                  class="items-start gap-2"
                  onclick={() => selectVariant(variant)}
                >
                  <Check
                    class="mt-0.5 size-3.5 shrink-0 {variant.id === selectedId
                      ? 'opacity-100'
                      : 'opacity-0'}"
                  />
                  <span class="min-w-0 break-words whitespace-normal leading-snug">
                    {bookVariantLabel(variant)}
                  </span>
                </DropdownMenu.Item>
              {/each}
            </DropdownMenu.Group>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </div>
    {/if}
  </div>

  <a
    tabindex="-1"
    class="block"
    href={href}
    use:route
    onclick={onCoverClick}
  >
    <h5
      id="title-{seriesid}-{num}-{subNum}"
      class="truncate text-center text-md font-semibold
      tracking-tight text-gray-900 dark:text-gray-300"
    >
      {type ? type[0].toUpperCase() : ''}{type.slice(1)}
      {num}{subNum ? '.' + subNum : ''}
    </h5>
  </a>
</div>
